import { getUserItem } from './userStore';
import { MODES } from './contentStore';

// Trois sources de premium, qui donnent (a une nuance de perimetre packs pres)
// les memes droits :
//
//  - `purchase`/`full_app` : l'achat one-time (RevenueCat). Definitif, ecrit par
//    le client.
//  - `purchase`/`patreon`  : membre du tier 15 $ du Patreon de Per. REVOCABLE,
//    ecrit par le serveur seul (le client ne fait que lire la row pullee), et
//    accompagne d'un `verified_at` qui borne sa validite offline.
//  - `purchase`/`beta`     : testeur beta ayant saisi un code d'invitation.
//    REVOCABLE (kill switch en Admin), ecrit par le client apres validation
//    serveur, avec un `verified_at` et une grace offline plus courte (7 jours).
//    N'ouvre que les packs du catalogue marques `beta_included`, pas les futurs
//    packs payants.
//
// Ne pas fusionner les cles : l'une est un achat, les autres des acces revocables.
//
// Lecture au moment de l'usage via le miroir user (jamais capturee a l'import).

export const FULL_APP_KEY = 'full_app';
export const PATREON_KEY = 'patreon';
export const BETA_KEY = 'beta';
// Prix de reference, tranche le 29/08/2026 (D1) : 30 EUR, chiffre rond pour la
// personne qui paie, et le meme sur les trois plateformes. Tenable partout :
// 30,00 EUR est un palier valide de l'App Store, Play laisse le prix libre, et
// RevenueCat Web Billing facture toutes taxes comprises hors US/Canada, donc
// l'europeen paie bien 30 EUR sur le web aussi.
//
// Ce label n'est qu'un repli : des que l'offering RevenueCat est chargee, c'est
// le prix localise du store qui s'affiche (purchases.js:getFullAppPriceLabel).
export const FULL_APP_PRICE_LABEL = '€30';

// Toutes les cles du scope 'purchase' qui ne sont PAS des ids de packs.
export const RESERVED_PURCHASE_KEYS = [FULL_APP_KEY, PATREON_KEY, BETA_KEY];

// Grace offline apres la derniere verification serveur reussie. C'est du confort
// produit (l'app doit marcher dans un avion), pas une garantie de securite.
export const PATREON_GRACE_DAYS = 30;
const GRACE_MS = PATREON_GRACE_DAYS * 24 * 60 * 60 * 1000;

// Grace beta plus courte : il faut pouvoir couper vite. Un testeur offline plus
// de 7 jours retombe en free et se re-valide a la prochaine connexion.
export const BETA_GRACE_DAYS = 7;
const BETA_GRACE_MS = BETA_GRACE_DAYS * 24 * 60 * 60 * 1000;

// Tonalites jouables sans achat : les 7 notes naturelles.
export const FREE_ROOT_NOTES = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

// Row Patreon telle que le serveur l'a posee. Absente des qu'un tombstone a ete
// pulle (le miroir n'hydrate pas les rows deleted).
export function getPatreonEntitlement() {
  return getUserItem('purchase', PATREON_KEY);
}

// Date de la derniere verification aupres de Patreon, ou null.
export function getPatreonVerifiedAt() {
  const at = getPatreonEntitlement()?.verified_at;
  const date = at ? new Date(at) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

// Actif = le serveur a dit oui, et ca date de moins de 30 jours. Sans
// `verified_at` exploitable on refuse : une row sans date ne peut pas expirer,
// et une row qui n'expire jamais n'est plus revocable.
export function hasPatreonAccess(now = Date.now()) {
  const row = getPatreonEntitlement();
  if (!row?.active) return false;
  const verifiedAt = getPatreonVerifiedAt();
  if (!verifiedAt) return false;
  return now - verifiedAt.getTime() < GRACE_MS;
}

// Row beta telle que le client l'a posee apres validation serveur. Absente des
// qu'un verify a renvoye active:false (le client supprime alors la row).
export function getBetaEntitlement() {
  return getUserItem('purchase', BETA_KEY);
}

// Date de la derniere verification serveur du code beta, ou null.
export function getBetaVerifiedAt() {
  const at = getBetaEntitlement()?.verified_at;
  const date = at ? new Date(at) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

// Actif = la row est active et sa derniere verification date de moins de 7 jours.
// Meme regle que Patreon (une row sans date exploitable ne peut pas expirer, donc
// on la refuse), avec une fenetre de grace plus courte.
export function hasBetaAccess(now = Date.now()) {
  const row = getBetaEntitlement();
  if (!row?.active) return false;
  const verifiedAt = getBetaVerifiedAt();
  if (!verifiedAt) return false;
  return now - verifiedAt.getTime() < BETA_GRACE_MS;
}

// Row de l'achat one-time telle qu'elle est posée dans le miroir, ou undefined.
// Son champ `source` dit d'où l'accès vient, et tout le monde n'a pas le même
// problème : 'revenuecat' est un vrai achat (store natif ET web billing, qui
// passent tous deux par le même reconcile de lib/purchases), 'mock' un achat
// simulé en build interne, 'admin' un accès offert par le serveur. Seul le
// premier est attaché à un compte de store et mérite qu'on propose de le
// sécuriser.
export function getFullAppPurchase() {
  return getUserItem('purchase', FULL_APP_KEY);
}

// Acces complet "dur" : achat one-time ou membre Patreon actif. Exclut la beta.
// Sert au perimetre packs : un acces beta n'ouvre PAS tous les packs bundled, il
// n'ouvre que ceux marques beta_included.
function hasNonBetaFullApp() {
  return !!getUserItem('purchase', FULL_APP_KEY) || hasPatreonAccess();
}

export function hasFullApp() {
  return hasNonBetaFullApp() || hasBetaAccess();
}

// Deverrouillage d'un pack, en tenant compte de la source d'acces :
//  - un pack IAP a la carte : deverrouille par son propre achat (`purchased`) ;
//  - un pack bundled : par l'app complete "dure" (achat ou Patreon) ;
//  - dans les deux cas, un acces beta l'ouvre UNIQUEMENT s'il porte beta_included.
// Un futur pack payant (ni bundled ni beta_included) reste verrouille pour un
// testeur beta : il faut l'acheter, comme tout le monde.
export function isPackUnlocked(pack, purchased = false) {
  if (!pack) return false;
  if (pack.bundled) {
    if (hasNonBetaFullApp()) return true;
  } else if (purchased) {
    return true;
  }
  if (pack.betaIncluded === true && hasBetaAccess()) return true;
  return false;
}

export function isModeUnlocked(modeName) {
  if (hasFullApp()) return true;
  return MODES[modeName]?.free === true;
}

export function isRootNoteUnlocked(rootNote) {
  if (hasFullApp()) return true;
  return FREE_ROOT_NOTES.includes(rootNote);
}
